import { z } from 'zod'

import { GetCompanySearchResultSchema } from './company.ts'

export const CompanyMergeFieldKeySchema = z.enum([
  'address',
  'country',
  'customer_care',
  'default_currency',
  'email',
  'hubspot_link',
  'notes',
  'patron',
  'phone',
  'registration_number',
  'short_title',
  'title',
  'vat',
  'website',
])

export const CompanyMergeCompanyTypeSchema = z.enum(['broker', 'haulier', 'other', 'shipper'])

export const CompanyMergeSideSchema = z.enum(['source', 'target'])

export const CompanyMergeXeroChoiceSchema = z.enum(['adopt_source', 'keep_target'])

export const CompanyMergePartyCountsSchema = z.object({
  contacts: z.number(),
  documents: z.number(),
  loads_as_carrier: z.number(),
  loads_as_customer: z.number(),
  locations: z.number(),
  members: z.number(),
  xero_bills: z.number(),
  xero_invoices: z.number(),
})

export const GetCompanyMergePartySchema = z.object({
  address: z.string().nullish(),
  counts: CompanyMergePartyCountsSchema,
  email: z.string().nullish(),
  id: z.number(),
  // A date-only string; read as local midnight so the calendar day doesn't shift west of UTC.
  last_used_at: z.iso
    .date()
    .transform(value => new Date(`${value}T00:00`))
    .nullish(),
  phone: z.string().nullish(),
  registration_number: z.string().nullish(),
  title: z.string(),
  type: CompanyMergeCompanyTypeSchema.nullish(),
  vat: z.string().nullish(),
  xero_linked: z.boolean(),
})

export const CompanyMergeFieldChoiceSchema = z.object({
  key: CompanyMergeFieldKeySchema,
  recommended: CompanyMergeSideSchema,
  source: z.string().nullish(),
  target: z.string().nullish(),
})

export const GetCompanyMergePreviewSchema = z.object({
  blockers: z.string().array(),
  fields: CompanyMergeFieldChoiceSchema.array(),
  locked: z.object({
    // Locked contacts of both the source and the target: contacts referenced by loads.
    contact_ids: z.number().array(),
  }),
  resulting_type: CompanyMergeCompanyTypeSchema.nullish(),
  source: GetCompanyMergePartySchema,
  target: GetCompanyMergePartySchema,
  warnings: z.string().array(),
  xero: z.enum(['adopt_source', 'keep_target', 'none']),
  xero_conflict: z.boolean(),
})

export const CompanyMergePreviewInputSchema = z.object({
  target_id: z.number(),
})

export const CompanyMergeInputSchema = CompanyMergePreviewInputSchema.extend({
  contact_ids: z.number().array().optional(),
  fields: CompanyMergeFieldKeySchema.array().optional(),
  xero: CompanyMergeXeroChoiceSchema.optional(),
})

export const CompanyDuplicateMatchReasonSchema = z.enum(['name', 'registration_number', 'vat'])

/** A likely duplicate from `GET /companies/:id/duplicates`: a company search result and why it matched. */
export const GetCompanyDuplicateSchema = GetCompanySearchResultSchema.extend({
  // Strongest first.
  match_reasons: CompanyDuplicateMatchReasonSchema.array(),
  registration_number: z.string().nullish(),
})

export const GetCompanyDuplicateDismissalSchema = z.object({
  company_id: z.number(),
  dismissed_at: z.string().nullish(),
  dismissed_by_user_id: z.number().nullish(),
  other_company_id: z.number(),
})

export type CompanyDuplicateMatchReason = z.infer<typeof CompanyDuplicateMatchReasonSchema>
export type CompanyMergeFieldChoice = z.infer<typeof CompanyMergeFieldChoiceSchema>
export type CompanyMergeFieldKey = z.infer<typeof CompanyMergeFieldKeySchema>
export type CompanyMergeInput = z.infer<typeof CompanyMergeInputSchema>
export type CompanyMergeSide = z.infer<typeof CompanyMergeSideSchema>
export type CompanyMergeXeroChoice = z.infer<typeof CompanyMergeXeroChoiceSchema>
export type GetCompanyDuplicate = z.infer<typeof GetCompanyDuplicateSchema>
export type GetCompanyMergeParty = z.infer<typeof GetCompanyMergePartySchema>
export type GetCompanyMergePreview = z.infer<typeof GetCompanyMergePreviewSchema>
